import { AsyncPipe } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, HostListener, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { DelayedScriptsComponent } from '@components/delayed-scripts';
import { FooterComponent } from '@components/footer';
import { NavigationComponent } from '@components/navigation';
import { PromotionComponent } from '@components/promotion';
import { environment } from '@environments/environment';
import { VARIABLES } from '@environments/environment.variables';
import { CacheService } from '@services/cache';
import { CommonService } from '@services/common';
import { MagnetisService } from '@services/magnetis';
import { ComponentLoaderService, componentsToLoad } from '@services/component-loader';
import { ContextService } from '@services/context';
import { HeadService } from '@services/head';
import { LogsService } from '@services/logs';
import { LogLevel } from '@services/logs/enums';
import { ResponsiveService } from '@services/responsive';
import { SeoService } from '@services/seo';
import { map } from 'rxjs';

const CDN_PRECONNECT = new URL(environment.CDN.url).origin;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [PromotionComponent, NavigationComponent, RouterOutlet, FooterComponent, AsyncPipe, DelayedScriptsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  readonly #componentLoaderService = inject(ComponentLoaderService);
  readonly #headService = inject(HeadService);
  readonly #seoService = inject(SeoService);
  readonly #commonService = inject(CommonService);
  dataMenu$ = this.#commonService.getCommon$().pipe(map((common) => common.menu));
  readonly #contextService = inject(ContextService);
  readonly #responsiveService = inject(ResponsiveService);
  readonly #logsService = inject(LogsService);
  readonly #cacheService = inject(CacheService);

  constructor() {
    inject(MagnetisService);
    afterNextRender(() => {
      const buildDate =
        VARIABLES.buildTimestamp === 0 ? 'local' : new Date(VARIABLES.buildTimestamp).toLocaleString('fr-FR');
      const version = VARIABLES.version || 'dev';
      const commitSegment =
        environment.logLevel >= LogLevel.DEBUG && VARIABLES.commitHash ? ` · ${VARIABLES.commitHash}` : '';
      // eslint-disable-next-line no-console -- intentional: surfaced to every user (incl. prod) for support purposes
      console.log(`🏷️ Build : ${version}${commitSegment} · ${buildDate}`);
      this.#logsService.trace('AppComponent', 'environment', environment);
    });
    this.#componentLoaderService.setComponents(componentsToLoad);
    this.#headService.insertPerformanceTrackingScript();
    this.#seoService.setPreconnectLink(CDN_PRECONNECT);
  }

  ngOnInit(): void {
    this.#responsiveService.onInit();
    void this.#cacheService.remove('middleOfficeToken');
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.#contextService.isBrowser()) {
      this.#responsiveService.onWindowResize();
    }
  }
}
